import device from '@tencent/device';
import { QBAppModule } from './jsApi';
import { QLIGHT_BASE_URL } from '@/entities/config';
import { Platform } from '@/entities/base';

/**
 * 获取查询参数
 * @param {String} name
 * @return {String}
 */
export const query = (name: string) => {
  if (typeof location === 'undefined' || !location.search) {
    return '';
  }
  let value = location.search.match(new RegExp(`(\\?|&)${name}=([^&]*)(&|$)`))
    ? decodeURIComponent(RegExp.$2)
    : '';

  if (value.match(/<\/?script>/i)) {
    console.warn(
      '参数中包含"<script>"为防止门神反射-XSS漏洞自动去除',
      name,
      value,
    );
    // 自动去除
    value = value.replace(/<\/?script>/gi, '');
  }

  return value;
};

export function debounce(
  func: {
    apply: (arg0: any, arg1: IArguments) => void;
  },
  wait: number | undefined,
  immediate: any,
) {
  let timeout: string | number | NodeJS.Timeout | null | undefined;
  return function () {
    const args = arguments;

    if (timeout) clearTimeout(timeout);
    if (immediate) {
      const callNow = !timeout;
      timeout = setTimeout(() => {
        timeout = null;
      }, wait);
      if (callNow) typeof func === 'function' && func.apply(null, args);
    } else {
      timeout = setTimeout(() => {
        typeof func === 'function' && func.apply(null, args);
      }, wait);
    }
  };
}

/**
 * 函数限流
 * @param {function} fn 函数
 * @param {number} defer 单位时间，毫秒
 * @param {boolean} isInvokeAfterDefer 超过单位时间后是否默认调用
 */
export function throttle(
  fn: { (): void; apply?: any },
  defer: number,
  isInvokeAfterDefer = true,
) {
  let lastTime: number;
  let timer: string | number | NodeJS.Timeout | undefined;

  return function (...args: any) {
    const now = Date.now();
    const gap = now - lastTime;

    if (lastTime && gap < defer) {
      if (timer) {
        clearTimeout(timer);
      }

      if (isInvokeAfterDefer) {
        // 满足时间间隔要求再执行
        timer = setTimeout(() => {
          typeof fn === 'function' && fn.apply(null, args);
        }, defer - gap);
      }

      return;
    }

    lastTime = now;

    if (timer) {
      clearTimeout(timer);
    }

    typeof fn === 'function' && fn.apply(null, args);
  };
}
interface ScriptParams {
  url: string;
  crossOrigin?: boolean;
  retry?: boolean;
}
/**
 * 加载脚本
 * @param {Object} opts
 * @param {String} opts.url
 * @param {Boolean} opts.crossOrigin 是否设置crossOrigin = anonymous
 */
export const loadScript: any = (params: string | ScriptParams) => {
  let opts: { crossOrigin?: any; retry?: any; url?: any };
  if (typeof params === 'string') {
    opts = {
      url: params,
      crossOrigin: true,
      retry: true,
    };
  } else {
    opts = params;
  }
  const { url } = opts;
  loadScript.loading = loadScript.loading || {};
  return (loadScript.loading[url] = loadScript.loading[url] || new Promise((resolve, reject) => {
    const script: any = document.createElement('script');
    const onload = function () {
      setTimeout(resolve, 30);
    };
    if ('onload' in script) {
      script.onload = onload;
    } else {
      script.onreadystatechange = function () {
        if (this.readyState === 'loaded' || this.readyState === 'complete') {
          onload();
        }
      };
    }
    script.onerror = reject;
    script.type = 'text/javascript';
    script.async = false;
    if (opts.crossOrigin) {
      script.crossOrigin = 'anonymous';
    }
    script.src = url;
    document.getElementsByTagName('head')[0].appendChild(script);
  }).catch(() => {
    // retry
    if (opts.retry) {
      loadScript.loading[url] = null;
      return loadScript(Object.assign({}, opts, {
        retry: false,
      }));
    }
    return Promise.reject();
  }));
};

/**
 * 获取url上的参数
 * @param {string} url
 */
export const getParams = (url: string | string[]) => {
  const searchIndex = url.indexOf('?');

  if (searchIndex < 0) {
    return {};
  }

  const search = url.slice(searchIndex + 1);

  if (search === '') {
    return {};
  }

  const searchArr = (search as string).split('&');

  const params: any = {};
  const reg = /(.+)=(.+)/;

  searchArr.forEach((item: string) => {
    const result = reg.exec(item);

    if (result) {
      params[result[1]] = result[2];
    }
  });

  return params;
};

/**
 * 为url添加参数
 * @param {string} url
 * @param {object} params
 */
export const setParams = (url: string | string[], params: any) => {
  const finalParams = Object.assign({}, getParams(url), params);

  let paramStr = '';
  let index = 0;

  for (const k in finalParams) {
    if (typeof finalParams[k] === 'undefined') {
      continue;
    }
    paramStr += `${index === 0 ? '' : '&'}${k}=${finalParams[k]}`;
    index += 1;
  }

  const searchIndex = url.indexOf('?');

  if (searchIndex < 0) {
    return `${url}?${paramStr}`;
  }
  return `${url.slice(0, url.indexOf('?'))}?${paramStr}`;
};

// 分转元的单元转换
export const money = (fen: string | number): string => {
  if (!fen) {
    return '0';
  }
  let num: any = +fen / 100;
  num += '';
  const reg = num.indexOf('.') > -1 ? /(\d{1,3})(?=(?:\d{3})+\.)/g : /(\d{1,3})(?=(?:\d{3})+$)/g;
  num = num.replace(reg, '$1');
  return num;
};

export function once(fn: any) {
  // 利用闭包判断函数是否执行过
  let called = false;
  return function () {
    if (!called) {
      called = true;
      fn.apply(null, arguments);
    }
  };
}

export function openUrl(url: string) {
  if (device.isQQBrowser) {
    QBAppModule.openUrl({
      url: getQlightByUrl(url),
      newWindow: device.isIOS ? '0' : '1', // 是否新窗口打开
    });
  } else {
    window.location.href = url;
  }
}

export function getQlightByUrl(url: string) {
  if (!url) {
    return '';
  }
  if (url.indexOf('qb://qlight') > -1) {
    return '';
  }
  if (url.indexOf('qb://ext/rn') > -1) {
    console.log('🚀 ~ file: util.ts:260 ~ getQlightByUrl ~ url:', url);
    return url;
  }
  return `${QLIGHT_BASE_URL}${encodeURIComponent(url)}`;
}
export function getScrollTop() {
  let scrollTop = 0; let bodyScrollTop = 0; let documentScrollTop = 0;
  if (document.body) {
    bodyScrollTop = document.body.scrollTop;
  }
  if (document.documentElement) {
    documentScrollTop = document.documentElement.scrollTop;
  }
  scrollTop = (bodyScrollTop - documentScrollTop > 0) ? bodyScrollTop : documentScrollTop;
  return scrollTop;
}

export function getScrollHeight() {
  let scrollHeight = 0; let bodyScrollHeight = 0; let documentScrollHeight = 0;
  if (document.body) {
    bodyScrollHeight = document.body.scrollHeight;
  }
  if (document.documentElement) {
    documentScrollHeight = document.documentElement.scrollHeight;
  }
  scrollHeight = (bodyScrollHeight - documentScrollHeight > 0) ? bodyScrollHeight : documentScrollHeight;
  return scrollHeight;
}

export function getWindowHeight() {
  let windowHeight = 0;
  if (document.compatMode == 'CSS1Compat') {
    windowHeight = document.documentElement.clientHeight;
  } else {
    windowHeight = document.body.clientHeight;
  }
  return windowHeight;
}

export const dateTimeformat = (dateTime: Date, fmtParams: string) => {
  const o: { [key: string]: number } = {
    'M+': dateTime.getMonth() + 1, // 月份
    'd+': dateTime.getDate(), // 日
    'h+': dateTime.getHours(), // 小时
    'm+': dateTime.getMinutes(), // 分
    's+': dateTime.getSeconds(), // 秒
    'q+': Math.floor((dateTime.getMonth() + 3) / 3), // 季度
    S: dateTime.getMilliseconds(), // 毫秒
  };

  let fmt = '';
  if (/(y+)/.test(fmtParams)) {
    fmt = fmtParams.replace(RegExp.$1, String(dateTime.getFullYear()).substr(4 - RegExp.$1.length));
  }

  Object.keys(o).forEach((k: string) => {
    if (new RegExp(`(${k})`).test(fmt)) {
      fmt = fmt.replace(
        RegExp.$1,
        RegExp.$1.length === 1 ? String(o[k]) : String(`00${o[k]}`.substr(String(o[k]).length)),
      );
    }
  });

  return fmt;
};

export const parseItemInfo = () => {
  let location = window.location.href;
  if (location.match(/<\/?script>/i)) {
    console.warn(
      '参数中包含"<script>"为防止门神反射-XSS漏洞自动去除',
      name,
      location,
    );
    // 自动去除
    location = location.replace(/<\/?script>/gi, '');
  }
  const itemInfoStr = decodeURIComponent(location.split('item_info=')[1]);
  const itemInfoStrLeft = itemInfoStr.split('true}')[0];
  return decodeURIComponent(`${itemInfoStrLeft}true}`);
};

export function getCookie(name: string) {
  // Split cookie string and get all individual name=value pairs in an array
  const cookieArr = document.cookie.split(';');
  console.log('🚀 ~ file: util.ts:345 ~ getCookie ~ document.cookie:', document.cookie);

  // Loop through the array elements
  for (let i = 0; i < cookieArr.length; i++) {
    const cookiePair = cookieArr[i].split('=');

    /* Removing whitespace at the beginning of the cookie name
      and compare it with the given string */
    if (name == cookiePair[0].trim()) {
      // Decode the cookie value and return
      return decodeURIComponent(cookiePair[1]);
    }
  }

  // Return null if not found
  return '';
}

// 如果计算时候小于10，前面补个0，常用于时间的表示
const addZeroInUnit = (num: number): string => {
  if (num < 10) {
    return `0${num}`;
  }
  return num.toString();
};

export const countDownTime = (time = 0, cur = 0) => {
  if (time <= 0 && cur <= 0) {
    return '';
  }
  const curTime = new Date(cur * 1000);
  const endTime = new Date(time * 1000);

  if (curTime >= endTime) {
    return '';
  }
  const lefttime = endTime.getTime() - curTime.getTime();  // 距离结束时间的毫秒数
  const leftd = Math.floor(lefttime / (1000 * 60 * 60 * 24));  // 计算天数
  const lefth = Math.floor((lefttime / (1000 * 60 * 60)) % 24);  // 计算小时数
  const leftm = Math.floor((lefttime / (1000 * 60)) % 60);  // 计算分钟数
  const lefts = Math.floor((lefttime / 1000) % 60);  // 计算秒数
  return `${leftd}天${addZeroInUnit(lefth)}:${addZeroInUnit(leftm)}:${addZeroInUnit(lefts)}`;  // 返回倒计时的字符串
};

export function OS() {
  const agent = navigator.userAgent.toLowerCase();
  const isMac = /macintosh|mac os x/i.test(navigator.userAgent);
  if (agent.indexOf('win32') >= 0 || agent.indexOf('wow32') >= 0) {
    return 'WIN';
  }
  if (agent.indexOf('win64') >= 0 || agent.indexOf('wow64') >= 0) {
    return 'WIN';
  }
  if (isMac) {
    return 'MAC';
  }
}

export function OSNum() {
  const agent = navigator.userAgent.toLowerCase();
  const isMac = /macintosh|mac os x/i.test(navigator.userAgent);
  if (agent.indexOf('win32') >= 0 || agent.indexOf('wow32') >= 0) {
    return Platform.WinPlatform;
  }
  if (agent.indexOf('win64') >= 0 || agent.indexOf('wow64') >= 0) {
    return Platform.WinPlatform;
  }
  if (isMac) {
    return Platform.MacPlatform;
  }
  if (device.isAndroid) {
    return Platform.AdrPlatform;
  }
  if (device.isIOS) {
    return Platform.IosPlatform;
  }
}

export enum UnitEnum {
  's' = 'second',
  'm' = 'minute',
  'd' = 'day',
}

export enum SymbolEnum {
  'POINT' = '.',
  'LINE' = '-',
}

export const coverTimeToSecond = (time: number, unit?: UnitEnum, symbol: SymbolEnum = SymbolEnum.POINT): string => {
  const originTime = new Date(time * 1000);
  switch (unit) {
    case UnitEnum.d:
      return `${originTime.getFullYear()}${symbol}${addZeroInUnit(originTime.getMonth() + 1)}${symbol}${addZeroInUnit(originTime.getDate())}`;
    case UnitEnum.m:
      return `${originTime.getFullYear()}${symbol}${originTime.getMonth() + 1}${symbol}${originTime.getDate()} ${addZeroInUnit(originTime.getHours())}:${addZeroInUnit(originTime.getMinutes())}`;
    case UnitEnum.s:
      return `${originTime.getFullYear()}${symbol}${originTime.getMonth() + 1}${symbol}${originTime.getDate()} ${addZeroInUnit(originTime.getHours())}:${addZeroInUnit(originTime.getMinutes())}:${addZeroInUnit(originTime.getSeconds())}`;
    default:
      return `${originTime.getFullYear()}${symbol}${originTime.getMonth() + 1}${symbol}${originTime.getDate()}`;
  }
};

export const getDaysBetweenByService = (time: number, now: number) => Math.round((now - time) / (1 * 24 * 60 * 60));

/**
 *
 * 是否是PCQB
 * @param ua ua字符串
 */
export const isQQBrowser = (ua = ''): boolean => {
  // @ts-ignore. 通过qua判断
  if (window?.external?.getQUA?.()?.indexOf('PR=PC') > -1) {
    return true;
  }
  if (ua.indexOf('QQBrowser') > -1) {
    return true;
  }
  return false;
};

export const changeSize = (limit: number) => {
  let size = '';
  if (limit < 1 * 1024) {                            // 小于1KB，则转化成B
    size = `${limit.toFixed(0)}B`;
  } else if (limit < 1 * 1024 * 1024) {            // 小于1MB，则转化成KB
    size = `${(limit / 1024).toFixed(0)}KB`;
  } else if (limit < 1 * 1024 * 1024 * 1024) {        // 小于1GB，则转化成MB
    size = `${(limit / (1024 * 1024)).toFixed(0)}MB`;
  } else if (limit < 1 * 1024 * 1024 * 1024 * 1024) {        // 小于1GB，则转化成MB
    size = `${(limit / (1024 * 1024 * 1024)).toFixed(0)}GB`;
  } else {                                            // 其他转化成GB
    size = `${(limit / (1024 * 1024 * 1024 * 1024)).toFixed(0)}TB`;
  }

  const sizeStr = `${size}`;                        // 转成字符串
  const index = sizeStr.indexOf('.');                    // 获取小数点处的索引
  const dou = sizeStr.substr(index + 1, 2);            // 获取小数点后两位的值
  if (dou == '00') {                                // 判断后两位是否为00，如果是则删除00
    return sizeStr.substring(0, index) + sizeStr.substr(index + 3, 2);
  }
  return size;
};
